import { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { PageHeader, Badge } from "@/components/common";
import { Link } from "react-router-dom";
import {
  Boxes,
  TrendingUp,
  Wallet,
  ClipboardList,
  Store,
  ArrowUpRight,
  Package,
  Layers,
  Award,
} from "lucide-react";
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, sub, color, testid }) => (
  <div
    data-testid={testid}
    className="relative overflow-hidden rounded-xl border border-slate-800 bg-[#131822] p-5 hover:border-slate-700 transition-all animate-fade-up"
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0">
        <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{label}</p>
        <p className="font-mono text-2xl font-black text-white mt-2 truncate">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </div>
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center shrink-0 ${color}`}>
        <Icon className="w-5 h-5" />
      </div>
    </div>
  </div>
);

export default function Dashboard() {
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard").then((r) => setData(r.data)).catch(() => {});
  }, []);

  if (!data)
    return (
      <div className="flex items-center justify-center h-96 text-slate-500 font-mono text-sm">Memuat data...</div>
    );

  const catData = [
    { key: "sealed", label: "Sealed", val: data.inventory.by_kategori.sealed, cls: "text-indigo-300", bar: "bg-indigo-500" },
    { key: "raw", label: "Raw", val: data.inventory.by_kategori.raw, cls: "text-emerald-300", bar: "bg-emerald-500" },
    { key: "slab", label: "Slab", val: data.inventory.by_kategori.slab, cls: "text-amber-300", bar: "bg-amber-500" },
  ];
  const totalCat = catData.reduce((a, b) => a + b.val, 0) || 1;

  return (
    <div>
      <PageHeader title="Dashboard" subtitle="Ringkasan Bisnis" />

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard
          testid="stat-stock-value"
          icon={Wallet}
          label="Nilai Stok (Modal)"
          value={formatRupiah(data.inventory.total_modal)}
          sub={`${data.inventory.total_stok_qty} unit • ${data.inventory.jumlah_produk} produk`}
          color="bg-indigo-500/15 text-indigo-300"
        />
        <StatCard
          testid="stat-sales-mtd"
          icon={TrendingUp}
          label="Penjualan Bulan Ini"
          value={formatRupiah(data.penjualan.bulan_ini)}
          sub={`${data.penjualan.jumlah_transaksi_bulan} transaksi`}
          color="bg-emerald-500/15 text-emerald-300"
        />
        <StatCard
          testid="stat-profit-mtd"
          icon={ArrowUpRight}
          label="Laba Bulan Ini"
          value={formatRupiah(data.penjualan.laba_bulan_ini)}
          sub="Setelah modal barang"
          color="bg-amber-500/15 text-amber-300"
        />
        <StatCard
          testid="stat-preorder"
          icon={ClipboardList}
          label="Pre-Order Aktif"
          value={data.preorder.aktif}
          sub={`DP terkumpul ${formatRupiah(data.preorder.dp_terkumpul)}`}
          color="bg-cyan-500/15 text-cyan-300"
        />
      </div>

      {/* Sales by channel recap */}
      <div className="rounded-xl border border-slate-800 bg-[#131822] p-5 mb-6">
        <h3 className="font-heading font-semibold text-white mb-4">Rekap Penjualan per Channel (Bulan Ini)</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
          {[
            { k: "event", label: "Event", cls: "text-indigo-300 border-indigo-500/30" },
            { k: "direct", label: "Direct / Toko", cls: "text-emerald-300 border-emerald-500/30" },
            { k: "tokopedia", label: "Tokopedia", cls: "text-green-300 border-green-500/30" },
            { k: "shopee", label: "Shopee", cls: "text-orange-300 border-orange-500/30" },
            { k: "live", label: "Live Selling", cls: "text-rose-300 border-rose-500/30" },
          ].map((c) => (
            <div key={c.k} className={`rounded-lg border bg-slate-950/40 p-4 ${c.cls}`} data-testid={`channel-${c.k}`}>
              <p className="text-xs font-mono uppercase tracking-wider text-slate-500">{c.label}</p>
              <p className="font-mono text-lg font-black mt-1.5">{formatRupiah((data.penjualan.by_channel || {})[c.k] || 0)}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Sales trend */}
        <div className="lg:col-span-2 rounded-xl border border-slate-800 bg-[#131822] p-5">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-white">Tren Penjualan (7 Hari)</h3>
            <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30">Harian</Badge>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data.trend} margin={{ left: -10, right: 10, top: 10 }}>
                <defs>
                  <linearGradient id="col" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#6366F1" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#6366F1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#1E2735" vertical={false} />
                <XAxis dataKey="hari" stroke="#64748B" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis
                  stroke="#64748B"
                  fontSize={11}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(v) => (v >= 1000000 ? `${v / 1000000}jt` : v >= 1000 ? `${v / 1000}rb` : v)}
                />
                <Tooltip
                  contentStyle={{ background: "#0F131C", border: "1px solid #2D3748", borderRadius: 10, color: "#fff" }}
                  formatter={(v) => [formatRupiah(v), "Penjualan"]}
                />
                <Area type="monotone" dataKey="total" stroke="#6366F1" strokeWidth={2.5} fill="url(#col)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Stock composition */}
        <div className="rounded-xl border border-slate-800 bg-[#131822] p-5">
          <h3 className="font-heading font-semibold text-white mb-4">Komposisi Stok</h3>
          <div className="space-y-4">
            {catData.map((c) => (
              <div key={c.key}>
                <div className="flex justify-between text-sm mb-1.5">
                  <span className={`font-medium ${c.cls}`}>{c.label}</span>
                  <span className="font-mono text-slate-400">{c.val} unit</span>
                </div>
                <div className="h-2 rounded-full bg-slate-800 overflow-hidden">
                  <div className={`h-full rounded-full ${c.bar}`} style={{ width: `${(c.val / totalCat) * 100}%` }} />
                </div>
              </div>
            ))}
          </div>
          <div className="mt-6 pt-4 border-t border-slate-800">
            <p className="text-xs font-mono uppercase tracking-wider text-slate-500">Estimasi Nilai Jual</p>
            <p className="font-mono text-xl font-black text-emerald-300 mt-1">
              {formatRupiah(data.inventory.total_nilai_jual)}
            </p>
          </div>
        </div>
      </div>

      {/* Active event */}
      <div className="mt-6">
        {data.active_event ? (
          <Link
            to={`/events/${data.active_event.id}`}
            data-testid="active-event-card"
            className="block rounded-xl border border-emerald-500/30 bg-gradient-to-r from-emerald-500/10 via-[#131822] to-[#131822] p-6 hover:border-emerald-500/60 transition-all"
          >
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <div>
                <div className="flex items-center gap-2 mb-2">
                  <Store className="w-4 h-4 text-emerald-400" />
                  <Badge className="bg-emerald-500/10 text-emerald-300 border-emerald-500/30 animate-pulse">
                    Event Aktif
                  </Badge>
                </div>
                <h3 className="font-heading text-xl font-bold text-white">{data.active_event.nama_event}</h3>
                <p className="text-sm text-slate-400 mt-0.5">{data.active_event.lokasi}</p>
              </div>
              <div className="flex gap-8">
                <div>
                  <p className="text-xs font-mono uppercase text-slate-500">Penjualan</p>
                  <p className="font-mono text-lg font-bold text-white">{formatRupiah(data.active_event.total_penjualan)}</p>
                </div>
                <div>
                  <p className="text-xs font-mono uppercase text-slate-500">Laba Bersih</p>
                  <p className={`font-mono text-lg font-bold ${data.active_event.laba_bersih >= 0 ? "text-emerald-300" : "text-rose-300"}`}>
                    {formatRupiah(data.active_event.laba_bersih)}
                  </p>
                </div>
              </div>
            </div>
          </Link>
        ) : (
          <div className="rounded-xl border border-slate-800 bg-[#131822] p-6 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-slate-800 flex items-center justify-center">
                <Store className="w-5 h-5 text-slate-500" />
              </div>
              <div>
                <p className="text-sm font-medium text-slate-300">Tidak ada event aktif</p>
                <p className="text-xs text-slate-500">Buat event card show untuk mulai mencatat penjualan.</p>
              </div>
            </div>
            <Link to="/events" className="text-sm font-medium text-indigo-400 hover:text-indigo-300">
              Ke Event →
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
